import { SmartSuggestService } from "core-library/components/smart-suggest/smart-suggest.service";
import { debounce } from "lodash";
import {DirectorySuggestionModel} from "../../../core-library/components/smart-suggest/directory-suggestion.model";
import {BaseSuggestionResultsModel} from "../../../core-library/components/smart-suggest/base-suggestion-results.model";
import {BaseSuggestionModel} from "../../../core-library/components/smart-suggest/base-suggestion.model";

export class SmartSuggestComponent {
    private _directorySearchApiUrl: string = null;
    private _directoryDetailsPageUrl: string = null;
    private _contentSearchApiUrl: string = null;
    private _searchPageUrl: string = null;
    private _adultSearchPageUrl: string = null;
    private _childrenSearchPageUrl: string = null;
    private _homePageId: string = null;
    private _context: JQuery<HTMLFormElement> = null;

    private _input: JQuery<HTMLInputElement> = null;
    private _suggestions: JQuery<HTMLLIElement> = null;
    private _searchCategorySelect: JQuery<HTMLSelectElement> = null;

    private _selectedLocations: Set<string> = new Set();
    private _selectedSearchCategory: string = null;
    private _directorySearchEnabled: boolean = false;

    private readonly _classMap = {
        input: ".js-pcg-search-bar__input",
        suggestions: ".js-pcg-smart-search",
        locationSuggestions: ".js-pcg-smart-search-location",
        searchCategorySelect: ".js-pcg-search-bar__search-category-select"
    };

    private debounceSetSuggestions = debounce((searchText: string) => this.setSuggestions(searchText), 700);

    public static init(context: JQuery<HTMLFormElement>, homePageId: string, directorySearchApiUrl: string, directoryDetailsPageUrl: string,  adultSearchPageUrl: string, contentSearchApiUrl: string, childrenSearchPageUrl: string): SmartSuggestComponent {
        const component = new SmartSuggestComponent();
        component.init(context, homePageId, directorySearchApiUrl, directoryDetailsPageUrl, adultSearchPageUrl, contentSearchApiUrl, childrenSearchPageUrl);
        return component;
    }

    public init(context: JQuery<HTMLFormElement>, homePageId: string, directorySearchApiUrl: string, directoryDetailsPageUrl: string, adultSearchPageUrl: string, contentSearchApiUrl: string, childrenSearchPageUrl: string): void {
        this._context = context || this._context;
        this._homePageId = homePageId || this._homePageId;
        this._directorySearchApiUrl = directorySearchApiUrl || this._directorySearchApiUrl;
        this._directoryDetailsPageUrl = directoryDetailsPageUrl || this._directoryDetailsPageUrl;
        this._adultSearchPageUrl = adultSearchPageUrl || this._adultSearchPageUrl;
        this._contentSearchApiUrl = contentSearchApiUrl || this._contentSearchApiUrl;
        this._childrenSearchPageUrl = childrenSearchPageUrl || this._childrenSearchPageUrl;

        this.parseContext();
        this.bindEvents();

        this._selectedSearchCategory = this._searchCategorySelect.val() as string;
        this.setSearchPageUrl();
    }

    private parseContext(): void {
        this._input = $(this._classMap.input, this._context);
        this._suggestions = $(this._classMap.suggestions, this._context);
        this._searchCategorySelect = $(this._classMap.searchCategorySelect, this._searchCategorySelect);
    }

    private bindEvents(): void {
        this._input
            .on("keyup", (event) => {
                const searchText = event.target.value.toString().trim();
                if (searchText) {
                    this.debounceSetSuggestions(searchText);
                }
            })
            .on("focusin", (event) => {
                const searchText = event.target.value.toString().trim();
                if (searchText) {
                    this.setSuggestions(searchText);
                }
            })
            .on("focusout", () => setTimeout(() => this.clearSuggestions(), 150)); // workaround to prevent clear before click on link triggered

        this._searchCategorySelect
            .on("change", () => {
                this._selectedSearchCategory = this._searchCategorySelect.val() as string;
                this.setSearchPageUrl();
            });

        this._context.on("submit", (event) => {
            event.preventDefault();

            const inputValue = this._input.val() as string;
            const locationParams = Array.from(this._selectedLocations)
                .map((id, index) => `locationIds.${index}=${id}`)
                .join("&");

            let url = "";
            switch (this._selectedSearchCategory){
                case "Adult":
                    url = `${this._searchPageUrl}?searchTab=directory&searchText=${inputValue.trim()}&${locationParams}`;
                    break;
                case "Children":
                    url = `${this._searchPageUrl}?searchText=${inputValue.trim()}`;
            }

            if (url){
                window.location.href = url;
            }
        });
    }

    private setSearchPageUrl(): void{
        switch (this._selectedSearchCategory) {
            case "Adult":
                this._searchPageUrl = this._adultSearchPageUrl;
                this._directorySearchEnabled = true;
                break;
            case "Children":
                this._searchPageUrl = this._childrenSearchPageUrl;
                this._directorySearchEnabled = false;
                break;
        }

        if (this._searchPageUrl){
            this._context.attr("action", this._searchPageUrl);
        }
    }

    private clearSuggestions(): void {
        this._suggestions.html("");
        this._suggestions.removeClass("show");
    }

    private showSuggestions(): void {
        this._suggestions.addClass("show");
    }

    private setSuggestions(searchText: string): void {
        if (searchText.length <= 2) {
            return;
        }

        if (this._selectedLocations.size > 0) {
            return;
        }
        this.clearSuggestions();

        const contentSearchParams = {
            searchContext: this._homePageId,
            searchText: searchText,
            pageNumber: 1,
            pageSize: 3,
            sortOption: "0",
            categories: [
                this._selectedSearchCategory
            ]
        };

        const directorySearchParams = {
            searchText,
            pageNumber: 1,
            pageSize: 4,
            sortOption: "Rank;asc"
        };

        const promises: Array<Promise<void>> = [];

        const contentSearchPromise = new Promise<void>((resolve, reject) => {
            void $.ajax({
                url: this._contentSearchApiUrl,
                method: "POST",
                contentType: "application/json",
                data: JSON.stringify(contentSearchParams),
                success: (response: { value: BaseSuggestionResultsModel<BaseSuggestionModel> }) => {
                    const contentSuggestions = response.value.pageItems;
                    contentSuggestions.forEach(x => x.colorClass = "blue-dot");
                    const suggestionElements = SmartSuggestService.getSuggestionElements(contentSuggestions, "Suggested results - Information & Advice");
                    this._suggestions.append(suggestionElements.join(""));
                    resolve();
                },
                error: (xhr, status, error) => {
                    console.error("Error executing the request", status, error);
                    reject(error);
                }
            });
        });
        promises.push(contentSearchPromise);

        if (this._directorySearchEnabled){
            const directorySearchPromise = new Promise<void>((resolve, reject) => {
                void $.ajax({
                    url: `${this._directorySearchApiUrl}/get-list?searchParams=${encodeURIComponent(JSON.stringify(directorySearchParams))}`,
                    method: "GET",
                    contentType: "application/json",
                    success: (response: BaseSuggestionResultsModel<DirectorySuggestionModel>) => {
                        const directorySuggestions = response.pageItems.map(item => ({
                            ...item,
                            url: `${this._directoryDetailsPageUrl}?id=${item.id}`,
                            colorClass: this.getColorClass(item.properties)
                        }));
                        const suggestionElements = SmartSuggestService.getSuggestionElements(
                            directorySuggestions,
                            "Suggested results - Adults"
                        );
                        this._suggestions.append(suggestionElements.join(""));
                        resolve();
                    },
                    error: (xhr, status, error) => {
                        console.error("Error executing the request", status, error);
                        reject(error);
                    }
                });
            });
            promises.push(directorySearchPromise);
        }

        Promise.all(promises)
            .then(() => {
                let searchPageUrl = "";
                switch (this._selectedSearchCategory){
                    case "Adult":
                        searchPageUrl = `${this._searchPageUrl}?searchTab=directory&searchText=${searchText}`;
                        break;
                    case "Children":
                        searchPageUrl = `${this._searchPageUrl}?searchText=${searchText}`;
                }
                this._suggestions.append(SmartSuggestService.getShowMoreElement(searchPageUrl));
                this.showSuggestions();
            })
            .catch(error => {
                console.error("Error processing suggestions:", error);
            });
    }

    private getColorClass(properties: Array<{ alias: string, value: string }>): string {
        const templateId = properties.find(property => property.alias === "TemplateId")?.value?.toLowerCase();

        switch (templateId) {
            case "a39b9fa9-d61c-4ce8-af85-a80100a8bd04":
                return "purple-dot";
            case "5c474a39-9a5b-48e7-8e59-abea00826451":
                return "orange-dot";
            case "17aa4b91-19d9-405f-a5a4-a5ed00f373c8":
                return "pink-dot";
            case "e33a53ba-0e9c-4772-80f8-a5ed00f38f34":
                return "teal-dot";
            default:
                return "";
        }
    }
}
